import React, { useEffect, useState } from 'react'

import { useQuery } from '../hooks/useStrapiQuery'
import get from 'lodash/get'
import isEqual from 'lodash/isEqual'
import usePrevious from '../../hooks/common/usePrevious'

/**
 * @typedef {Object} PersonalityResponse
 * @property {PersonalityData} data
 */

/**
 * @typedef {Object} PersonalityData
 * @property {PersonalityType[]} personalityType
 */

/**
 * @typedef {Object} PersonalityType
 * @property {PersonalityTypeData} data
 */

/**
 * @typedef {Object} PersonalityTypeData
 * @property {PersonalityTypeDataAttributes} attributes
 */

/**
 * @typedef {Object} PersonalityTypeDataAttributes
 * @property {string} personalityTypeAcronym
 * @property {string} description
 * @property {string} reportLink
 * @property {string} fullPersonalityType
 * @property {string} tagLine
 * @property {string} imageUrl
 * @property {Tip[]} tips
 * @property {MajorResults} majorResults
 * @property {JobResults} jobResults
 */

/**
 * @typedef {Object} Tip
 * @property {string} id
 * @property {string} content
 * @property {string} type
 */

/**
 * @typedef {Object} JobResults
 * @property {JobResult} commonJobs
 * @property {JobResult} topJobs
 */

/**
 * @typedef {Object} JobResult
 * @property {string} id
 * @property {Jobs} jobs
 */

/**
 * @typedef {Object} Jobs
 * @property {JobsData[]} data
 */

/**
 * @typedef {Object} JobsData
 * @property {Job} attributes
 */

/**
 * @typedef {Object} Job
 * @property {string} title
 * @property {string} [description]
 * @property {string} [videoUrl]
 */

/**
 * @typedef {Object} MajorResults
 * @property {MajorResult} topMajors
 */

/**
 * @typedef {Object} MajorResult
 * @property {string} id
 * @property {Majors} majors
 */

/**
 * @typedef {Object} Majors
 * @property {MajorsData[]} data
 */

/**
 * @typedef {Object} MajorsData
 * @property {Major} attributes
 */

/**
 * @typedef {Object} Major
 * @property {string} title
 */

/**
 * @typedef {Object} TopMajors
 * @property {string} id
 * @property {string} description
 */

const fields = [
  'description',
  'personalityTypeAcronym',
  'reportLink',
  'fullPersonalityType',
  'tagLine',
  'imageUrl',
  'characterArchetype',
  {
    characteristics: ['documentId', 'title'],
  },
  {
    majorResults: [
      {
        topMajors: [
          'id',
          'description',
          {
            operation: 'majors',
            fields: ['title'],
            variables: {
              majorsFilters: {
                name: 'filters',
                type: 'MajorFiltersInput',
                value: {
                  publishedAt: {
                    notNull: true,
                  },
                  title: {
                    ne: null,
                  },
                },
              },
            },
          },
        ],
      },
    ],
  },
  {
    jobResults: [
      {
        commonJobs: [
          {
            operation: 'jobs',
            fields: ['title'],
            variables: {
              commonJobsFilters: {
                name: 'filters',
                type: 'JobFiltersInput',
                value: {
                  title: {
                    ne: null,
                  },
                  publishedAt: {
                    notNull: true,
                  },
                },
              },
            },
          },
        ],
      },
      {
        topJobs: [
          {
            operation: 'jobs',
            fields: ['title', 'description', 'videoUrl'],
            variables: {
              topJobsFilters: {
                name: 'filters',
                type: 'JobFiltersInput',
                value: {
                  title: {
                    ne: null,
                  },
                  publishedAt: {
                    notNull: true,
                  },
                },
              },
            },
          },
        ],
      },
    ],
  },
]

const defaultVariables = {
 status: {
    value: 'PUBLISHED',
    type: 'PublicationStatus',
  },
  filters: {
    value: {
      personalityTypeAcronym: {
        eq: 'ENTJ',
      },
    },
    commonJobsFilters: {
      publishedAt: {
        notNull: true,
      },
      title: {
        ne: null,
      },
    },
    topJobsFilters: {
      publishedAt: {
        notNull: true,
        },
      title: {
          ne: null,
        },
    },
    type: 'PersonalityTypeFiltersInput',
  },
}

const getVariables = personalityCode => {
  return {
    status: 'PUBLISHED',
    filters: {
      personalityTypeAcronym: {
        eq: personalityCode,
      },
    },
  }
}

const withPersonalityType = BaseComponent => props => {
  const operation = 'personalityTypes'

  const response = useQuery(
    operation,
    defaultVariables,
    {
      lazy: true,
    },
    fields,
  )

  const [personalityTypeResultData, setPersonalityTypeResultData] = useState(null)

  /**
   * PersonalityData
   * @type PersonalityData
   */

  const personalityResultData = get(response, 'data[0]]')
  const refetch = get(response, 'refetch')
  const loading = get(response, 'loading')

  const previousData = usePrevious(personalityResultData)
  useEffect(() => {
    if (!isEqual(personalityResultData, previousData)) {
      setPersonalityTypeResultData(personalityResultData)
    }
  }, [personalityResultData, previousData])

  const getPersonalityTypeData = personalityCode => {
    const variables = getVariables(personalityCode)

    refetch(variables)
  }

  return (
    <BaseComponent
      {...props}
      personalityResultData={personalityTypeResultData}
      getPersonalityTypeResults={getPersonalityTypeData}
      loadingPersonalityResultData={loading}
    />
  )
}

export default withPersonalityType



// WEBPACK FOOTER //
// ./src/utils/graphql/hocs/withPersonalityAssessmentResult.js